import React, { useEffect, useRef, useState, useMemo } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useApp } from "../../../../context/AppState";
import { parseCssStringToObject } from "../../../../helper";
import { DEFAULT_BLOCK_CSS } from "../../../../constant/constant";

const SubscriptionPickerBlock = ({ section, productIndex, productData }) => {
  const { control, setValue } = useFormContext();
  const { isMobile } = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const [openDirectionUp, setOpenDirectionUp] = useState(false);

  const parseSellingPlanId = (id) => {
    if(typeof id === "string") {
      return id?.split("/")?.pop();
    }
    return id;
  }

  const name = `${section?.type}-${productIndex}`;

  const isRequiredSellingPlan = useMemo(() => {
    return productData?.requiresSellingPlan;
  }, [productData]);

  const sellingPlans = useMemo(() => {
    return (
      productData?.sellingPlanGroups?.flatMap((group) => (group?.sellingPlans || group?.selling_plans) || [])
    );
  }, [productData]);

  useEffect(() => {
    if (isOpen && dropdownRef.current && containerRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const spaceBelow = windowHeight - containerRect.bottom;
      const dropdownHeight = dropdownRect.height || 200;

      if (spaceBelow < dropdownHeight) {
        setOpenDirectionUp(true); // not enough space, open upward
      } else {
        setOpenDirectionUp(false); // enough space, open downward
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const sellingPlans = productData?.sellingPlanGroups?.flatMap((group) => (group?.sellingPlans || group?.selling_plans) || []);

    setSelected(sellingPlans?.[0]);
    setValue(name, parseSellingPlanId(sellingPlans?.[0]?.id) || null);
  }, [productData]);


  const blockCSS = {
    ...DEFAULT_BLOCK_CSS,
    ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
    ...parseCssStringToObject(
      section?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
    ),
  };

  const dropdownHeaderStyle = {
    ...blockCSS,
    padding: "0px 10px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "background-color 0.2s ease, color 0.2s ease",
    ...(isOpen && {
      backgroundColor: blockCSS?.backgroundColor ?? "#f0f0f0",
      color: blockCSS?.color ?? "#000000",
    }),
    border: "none !important",
    borderStyle: "none !important",
  };

  const dropdownListStyle = {
    borderRadius: 0,
    borderBottomLeftRadius: blockCSS.borderRadius,
    borderBottomRightRadius: blockCSS.borderRadius,
    position: "absolute",
    backgroundColor: "#FFFFFF",
    zIndex: 9999,
    width: "100%",
    maxHeight: "200px",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    left: 0,
    top: "100%",
    overflowY: "auto",
    transition: "opacity 0.2s ease, transform 1s ease",
  };

  const dropdownItemStyle = {
    ...blockCSS,
    textAlign: "start",
    borderRadius: "0px",
    justifyContent: "start",
    border: "none",
    padding: "10px",
    cursor: "pointer",
    transition:
      "background-color 0.3s ease, opacity 0.3s ease, transform 0.2s ease",
    borderStyle: "none !important",
  };

  const dropdownSearchStyle = {
    ...dropdownItemStyle,
    textAlign: "start",
    backgroundColor: "transparent",
  };

  const noDataStyle = {
    ...dropdownItemStyle,
    textAlign: "center",
    cursor: "default",
  };

  const handleSelect = (sellingPlan) => {
    setSelected(sellingPlan);
    setIsOpen(false);
  };

  const ArrowIcon = ({ isOpen }) => (
    <svg
      fill={blockCSS?.color ?? "#000000"}
      width={blockCSS?.fontSize ? `${parseInt(blockCSS.fontSize) - 5}` : "10px"}
      height={
        blockCSS?.fontSize ? `${parseInt(blockCSS.fontSize) - 5}` : "10px"
      }
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        pointerEvents: "none",
      }}
    >
      {isOpen ? (
        <path d="M11.178 19.569a.998.998 0 0 0 1.644 0l9-13A.999.999 0 0 0 21 5H3a1.002 1.002 0 0 0-.822 1.569l9 13z" />
      ) : (
        <path d="M11.178 4.431a.998.998 0 0 0-1.644 0l-9 13A.999.999 0 0 0 3 19h18a1.002 1.002 0 0 0 .822-1.569l-9-13z" />
      )}
    </svg>
  );

  if(!sellingPlans?.length) return null;

  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={parseSellingPlanId(sellingPlans?.[0]?.id)}
        render={({ field }) => (
          <div
            key={isOpen}
            ref={containerRef}
            style={{
              ...blockCSS,
              ...(isOpen && {
                borderBottomRightRadius: 0,
                borderBottomLeftRadius: 0,
              }),
            }}
          >
            <div style={dropdownHeaderStyle} onClick={() => setIsOpen(!isOpen)}>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "88%",
                  display: "inline-block",
                }}
              >
                {selected?.name || "Select Subscription Plan"}
              </span>
              <ArrowIcon isOpen={isOpen} />
            </div>

            {isOpen && (
              <div
                ref={dropdownRef}
                style={{
                  ...dropdownListStyle,
                  top: openDirectionUp ? "auto" : "100%",
                  bottom: openDirectionUp ? "100%" : "auto",
                }}
              >
                {sellingPlans?.length > 0 ? (
                  [
                    ...(sellingPlans || []),
                    ...(!isRequiredSellingPlan
                      ? [{ name: "One Time Purchase", id: null }]
                      : []),
                  ]?.map((sellingPlan, idx) => (
                    <div
                      key={idx}
                      style={dropdownItemStyle}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = "#fff";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor =
                          section?.setting.customCSS?.desktop?.backgroundColor;
                      }}
                      onClick={() => {
                        handleSelect(sellingPlan);
                        field.onChange(parseSellingPlanId(sellingPlan?.id));
                      }}
                    >
                      {sellingPlan.name}
                    </div>
                  ))
                ) : (
                  <div style={noDataStyle}>No Data</div>
                )}
              </div>
            )}
          </div>
        )}
      />
    </>
  );
};

export default SubscriptionPickerBlock;
